import React from "react";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import Login from "../pages/Login/Login";
import Dashboard from "../pages/Dashboard/Dashboard";
import HomePage from "../pages/Home/HomePage";
import UsersPage from "../pages/Users/UsersPage";
import RolesPage from "../pages/Roles/RolesPage";
import PermissionsPage from "../pages/Permissions/PermissionsPage";
import LookupSettingsPage from "../pages/Settings/LookupSettingsPage";
import TeacherSectionsPage from "../pages/Settings/TeacherSectionsPage";
import ClassConfigurationPage from "../pages/ClassConfiguration/ClassConfigurationPage";
import InstituteProfilePage from "../pages/Institute/InstituteProfilePage";
import AuditLogPage from "../pages/Audit/AuditLogPage";
import StudentFormPage from "../pages/Students/StudentFormPage";
import StaffFormPage from "../pages/Staff/StaffFormPage";
import SchedulerConfigPage from "../pages/Scheduler/SchedulerConfigPage";
import ZkDevicesPage from "../pages/ZkDevices/ZkDevicesPage";
import InstituteBillPage from "../pages/InstituteBill/InstituteBillPage";
import PlatformInstitutesPage from "../pages/PlatformInstitutes/PlatformInstitutesPage";
import BkashConfigPage from "../pages/Bkash/BkashConfigPage";
import OnlineFeeConfigPage from "../pages/Fpo/OnlineFeeConfigPage";
import LotteryPage from "../pages/Lottery/LotteryPage";
import OnlineAdmissionSettingsPage from "../pages/OnlineAdmission/OnlineAdmissionSettingsPage";
import OnlineAdmissionApplicantsPage from "../pages/OnlineAdmission/OnlineAdmissionApplicantsPage";
import OnlineAdmissionPage from "../pages/Public/OnlineAdmissionPage";
import StudentPortalPage from "../pages/Public/StudentPortalPage";
import ProtectedRoute from "./ProtectedRoute";
import SuperAdminRoute from "./SuperAdminRoute";
import PlatformOperatorRoute from "./PlatformOperatorRoute";
import DynamicPage from "./DynamicPage";

/**
 * Four kinds of route here, matching the real digicampusbd structure
 * (confirmed against the live schema, see MIGRATION.md):
 *  - /admin/* — fixed, super-admin-only routes for Users/Roles/Permissions/
 *    Institute profile/Audit log/bKash-SSLCommerz-Institute-Billing-ZK-Devices/
 *    Lottery/Online-Admission-Settings-Applicants, each confirmed (live query)
 *    to have zero real sidebar_item/child_nav_item rows — never part of the
 *    delegable Permission matrix. `/admin/settings/:resourceKey` and
 *    `/admin/class-configurations` are also kept here as a super-admin-fixed
 *    *alternate* path into pages that ARE also part of the delegable matrix —
 *    see the next bullet and MIGRATION.md's post-Phase-12 audit entry (a real
 *    regression: these were originally assumed super-admin-only too, but a
 *    live query proved regular delegated roles hold real grants on every one
 *    of these urls in production).
 *  - /students/new, /students/:id/edit, /staff/new, /staff/:id/edit — fixed
 *    sub-flows off the student/staff list pages (create/edit a specific
 *    record), reachable only by in-app navigation, same as digicampusbd's
 *    own edit actions never had their own menu entry either.
 *  - /public/* — Phase 10's first genuinely unauthenticated pages (online
 *    admission apply/status, student portal) — plain `<Route>`, no
 *    ProtectedRoute/SuperAdminRoute wrapper and no MainLayout shell, same as
 *    digicampusbd's own public-facing pages were never behind its session
 *    login either.
 *  - /:pageUrl — a single dynamic route mirroring digicampusbd's own
 *    `/{pageUrl}` catch-all, resolved against pageRegistry.js so the sidebar
 *    (built from GET /api/menu) never links to a dead route. This is where
 *    every real, delegable-Permission-matrix page lives — including the 14
 *    "Initial Setup" core-setting lookup pages and Class/Group Configuration,
 *    each also reachable via the fixed /admin/* path above for convenience.
 */
export default function AppRoutes() {
  return (
    <BrowserRouter>
      <Switch>
        <Route exact path="/login">
          <Login />
        </Route>

        <Route exact path="/public/online-admission">
          <OnlineAdmissionPage />
        </Route>
        <Route exact path="/public/student-portal">
          <StudentPortalPage />
        </Route>

        <ProtectedRoute exact path="/home">
          <HomePage />
        </ProtectedRoute>
        <ProtectedRoute exact path="/dashboard">
          <Dashboard />
        </ProtectedRoute>

        <SuperAdminRoute exact path="/admin/users">
          <UsersPage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/roles">
          <RolesPage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/permissions">
          <PermissionsPage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/settings/teacher-sections">
          <TeacherSectionsPage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/settings/:resourceKey">
          <LookupSettingsPage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/class-configurations">
          <ClassConfigurationPage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/institute-profile">
          <InstituteProfilePage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/audit-log">
          <AuditLogPage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/attendance-scheduler">
          <SchedulerConfigPage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/zk-devices">
          <ZkDevicesPage />
        </SuperAdminRoute>
        <PlatformOperatorRoute exact path="/admin/platform-institutes">
          <PlatformInstitutesPage />
        </PlatformOperatorRoute>
        <PlatformOperatorRoute exact path="/admin/institute-billing">
          <InstituteBillPage />
        </PlatformOperatorRoute>
        <PlatformOperatorRoute exact path="/admin/bkash-config">
          <BkashConfigPage />
        </PlatformOperatorRoute>
        <SuperAdminRoute exact path="/admin/online-fee-config">
          <OnlineFeeConfigPage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/lottery">
          <LotteryPage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/online-admission-settings">
          <OnlineAdmissionSettingsPage />
        </SuperAdminRoute>
        <SuperAdminRoute exact path="/admin/online-admission-applicants">
          <OnlineAdmissionApplicantsPage />
        </SuperAdminRoute>

        <ProtectedRoute exact path="/students/new">
          <StudentFormPage />
        </ProtectedRoute>
        <ProtectedRoute exact path="/students/:id/edit">
          <StudentFormPage />
        </ProtectedRoute>
        <ProtectedRoute exact path="/staff/new">
          <StaffFormPage />
        </ProtectedRoute>
        <ProtectedRoute exact path="/staff/:id/edit">
          <StaffFormPage />
        </ProtectedRoute>

        <Route exact path="/">
          <Redirect to="/home" />
        </Route>

        <ProtectedRoute exact path="/:pageUrl">
          <DynamicPage />
        </ProtectedRoute>

        <Route path="*">
          <Redirect to="/home" />
        </Route>
      </Switch>
    </BrowserRouter>
  );
}
